import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Form, ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { reduxForm, InjectedFormProps } from "redux-form";
import { connect, useDispatch, useSelector } from "react-redux";
import Header from "../../Includes/Header";
import BackButton from "../../Includes/BackButton";
import { ToastBox } from "MyComponents/Toaster";
import { setwizardPageNumber, setQuestionnaireScreen1 } from "../../authslice";
import { RootState } from "../../store";
import { updatePersonalKycDraftStep } from "Utils/personalKycDraft";
import questionnaire_icon from "../../../images/Questionnaire_icon.png";
import CheckboxGroup from "MyComponents/UI/CheckBoxGroup";

interface Props { }

const checkboxLabels = [
  { id: "checkbox1", label: "Investing" },
  { id: "checkbox2", label: "Payments & Transfers" },
  { id: "checkbox3", label: "Business payments" },
  { id: "checkbox4", label: "Online Purchase" },
  { id: "checkbox5", label: "Payment to friends" },
  { id: "checkbox6", label: "Online payments" },
  { id: "checkbox7", label: "Business" },
  { id: "checkbox8", label: "Savings & Investments" },
  { id: "checkbox9", label: "Make digital payment" },
  { id: "checkbox10", label: "Receive digital payment" },
  { id: "checkbox11", label: "Other" },
];

interface CheckboxState {
  [key: string]: boolean;
}

const Questionnaire_Screen1: React.FC<Props & InjectedFormProps<{}, Props, FormData>> = (props: any) => {
  const { t } = useTranslation();
  const { handleSubmit } = props;
  const dispatch = useDispatch();
  const page = useSelector((state: RootState) => state.auth.wizardPageNumber);
  const questionnaireScreen1 = useSelector((state: RootState) => state.auth.questionnaireScreen1);
  const userID = useSelector((state: RootState) => state.auth.userID);

  const [validated, setValidated] = useState(false);
  const [checkboxes, setCheckboxes] = useState<CheckboxState>({
    checkbox1: false, checkbox2: false, checkbox3: false, checkbox4: false,
    checkbox5: false, checkbox6: false, checkbox7: false, checkbox8: false,
    checkbox9: false, checkbox10: false, checkbox11: false,
  });

  useEffect(() => {
    const initialCheckboxState: CheckboxState = {};
    checkboxLabels.forEach(({ id }) => { initialCheckboxState[id] = false; });
    questionnaireScreen1.forEach((value: string) => {
      const matchingCheckbox = checkboxLabels.find((item) => item.label === value);
      if (matchingCheckbox) initialCheckboxState[matchingCheckbox.id] = true;
    });
    setCheckboxes(initialCheckboxState);
  }, [questionnaireScreen1]);

  const isAtLeastOneCheckboxChecked = Object.values(checkboxes).some((isChecked) => isChecked);

  const submitForm = () => {
    if (!isAtLeastOneCheckboxChecked) {
      setValidated(true);
    } else {
      const checkedCheckboxes = Object.entries(checkboxes)
        .filter(([_, value]) => value)
        .map(([key]) => checkboxLabels.find((item) => item.id === key)?.label);
      dispatch(setQuestionnaireScreen1(checkedCheckboxes as string[]));
      dispatch(setwizardPageNumber(page + 1));
      localStorage.setItem('pageNumber', `${page + 1}`);
      if (userID) {
        updatePersonalKycDraftStep(userID, 'questionnaire', { screen1: checkedCheckboxes });
        updatePersonalKycDraftStep(userID, 'wizardState', { pageNumber: page + 1 });
      }
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setValidated(false);
    setCheckboxes((prevCheckboxes) => ({ ...prevCheckboxes, [id]: checked }));
  };

  return (
    <>
      <ToastBox />
      <Container fluid className="px-0 px-md-3">
        <Row className="g-0">
          <Col md={12} className="mx-auto px-2 pt-0 pb-3">
            <div className="mt-5">
              <Row className="px-2 px-md-5">
                <Col md={12}><BackButton /></Col>
              </Row>
              <Row className="px-2 px-md-5">
                <Col md={12}>
                  <div className="d-flex align-items-start py-3">
                    <img src={questionnaire_icon} width={48} className="d-none d-md-block me-3 mt-1" alt="Questionnaire Icon" />
                    <div>
                      <h3 className="text-start m-0">{t('kyc.questionnaire')}</h3>
                      <p className="fw-normal text-secondary m-0 mt-1 text-nowrap">{t('kyc.individual_form')}</p>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="px-2 px-md-5">
                <Col md={12}>
                  <div className="d-none d-md-flex align-items-center">
                    <ProgressBar variant="primary" className="custom_progressbar mr-2" now={10} />
                    <p className="fw-bold small py-3 px-3 m-0">1/7</p>
                  </div>
                  <p className="fw-bold text-start mt-4 m-0">{t('kyc.q1_title')}</p>
                  <p className="text-secondary text-start small mt-1">{t('kyc.q1_desc')}</p>
                  {validated && (
                    <p className="text-danger text-start small fw-bold">{t('common.please_check_one')}</p>
                  )}
                  <Form noValidate onSubmit={handleSubmit(submitForm)}>
                    <Row className="mt-4 text-start align-items-center">
                      <CheckboxGroup
                        checkboxes={checkboxLabels.map((checkbox) => ({
                          ...checkbox,
                          checked: checkboxes[checkbox.id],
                        }))}
                        onCheckboxChange={handleCheckboxChange}
                      />
                    </Row>
                    <Row className="mt-4 text-start">
                      <Col md={8}>
                        <Button type="submit" className="btn btn-primary rounded-5" style={{ width: "220px" }}>
                          {t('common.next')}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const form = reduxForm<{}, Props, FormData>({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: "wizard",
  enableReinitialize: true,
})(Questionnaire_Screen1);

export default connect(null)(form);
